import { useLayoutEffect, useRef } from "react";

// The chat shell has to end at the bottom of the viewport, but neither app
// layout gives it a height to resolve a percentage against: AppLayout's content
// container only sets `min-height`, and MobileAppLayout sets none at all. The
// shell is also inside a `zoom: 95%` layout, so viewport units do not line up
// with what is actually on screen either. So measure instead: place the bottom
// edge at the viewport bottom, leaving any parent bottom padding we did not
// cancel (the mobile menu gutter) free.
const useFillViewportHeight = () => {
  const shellRef = useRef(null);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const applyHeight = () => {
      const parent = shell.parentElement;
      if (!parent) return;

      const rect = shell.getBoundingClientRect();
      // rect is in on-screen pixels, offsetHeight in layout pixels — their
      // ratio is the zoom the app layout applies to us.
      const scale = shell.offsetHeight ? rect.height / shell.offsetHeight : 1;
      if (!scale) return;

      const parentPaddingBottom =
        parseFloat(getComputedStyle(parent).paddingBottom) || 0;
      const shellMarginBottom =
        parseFloat(getComputedStyle(shell).marginBottom) || 0;
      // Negative margins cancel part of that padding; whatever is left has to
      // stay clear or the page gains a scrollbar.
      const reservedBelow = Math.max(
        0,
        parentPaddingBottom + shellMarginBottom,
      );

      const available = window.innerHeight - rect.top - reservedBelow * scale;
      const height = Math.max(320, available / scale);

      // Skipping no-op writes keeps the ResizeObserver below from ping-ponging
      const current = parseFloat(shell.style.height);
      if (!Number.isFinite(current) || Math.abs(current - height) > 1)
        shell.style.height = `${height}px`;
    };

    applyHeight();
    window.addEventListener("resize", applyHeight);

    // Anything that shifts the shell down (a toast above it, the sidebar
    // collapsing) changes where its top edge sits.
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(applyHeight)
        : null;
    if (observer && shell.parentElement) observer.observe(shell.parentElement);

    return () => {
      window.removeEventListener("resize", applyHeight);
      observer?.disconnect();
    };
  }, []);

  return shellRef;
};

export default useFillViewportHeight;
